import React, { ChangeEvent } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import Checkbox from 'components/Checkbox';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import { EAppointmentFormStatus, IForm } from 'modules/forms/interfaces/form.interfaces';
import { setFormData } from 'modules/forms/actions/form.actions';
import { infectedInsectBitesRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';

const SecondaryAssessment = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const stateForm = useTypedSelector(state => state.form.form);
  const methods = useForm<IForm>({
    defaultValues: {
      symptoms: stateForm.symptoms,
    },
    mode: 'onSubmit',
  });
  const { control, watch, setValue, handleSubmit } = methods;
  const symptoms = watch('symptoms') || [];

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    const status = stateForm.status || EAppointmentFormStatus.COMPLETED;
    dispatch(
      setFormData({
        ...values,
        status: values.symptoms?.includes('6') ? EAppointmentFormStatus.NEEDS_REVIEW : status,
      }),
    );
    history.push({
      pathname: infectedInsectBitesRoutes.photoUpload,
      search: history.location.search,
    });
  };

  const onChangeValue = (name: string, e: ChangeEvent<HTMLInputElement>) => {
    setValue(
      'symptoms',
      e.target.checked
        ? [...symptoms, name].filter(item => item !== '6')
        : symptoms.filter(item => item !== name),
    );
  };

  return (
    <Page>
      <PageHeader step={4} maxSteps={5} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock
                title="Assessment"
                description="Please confirm which of the following symptoms related to the Insect Bite(s) is the patient experiencing (select all that apply):"
              >
                <Controller
                  name="symptoms"
                  control={control}
                  render={() => (
                    <FormGroup>
                      <Checkbox
                        id="1"
                        name="1"
                        checked={symptoms?.includes('1')}
                        onChange={e => onChangeValue('1', e)}
                        label="Redness of skin"
                      />
                      <Checkbox
                        id="2"
                        name="2"
                        checked={symptoms?.includes('2')}
                        onChange={e => onChangeValue('2', e)}
                        label="Pain or tenderness to the area"
                      />
                      <Checkbox
                        id="3"
                        name="3"
                        checked={symptoms?.includes('3')}
                        onChange={e => onChangeValue('3', e)}
                        label="Swelling of skin"
                      />
                      <Checkbox
                        id="4"
                        name="4"
                        checked={symptoms?.includes('4')}
                        onChange={e => onChangeValue('4', e)}
                        label="Skin surrounding the bite feels hot to the touch"
                      />
                      <Checkbox
                        id="5"
                        name="5"
                        checked={symptoms?.includes('5')}
                        onChange={e => onChangeValue('5', e)}
                        label="The area of the Insect Bite appears infected"
                      />
                      <Checkbox
                        id="6"
                        name="6"
                        checked={symptoms?.includes('6')}
                        onChange={e => setValue('symptoms', e.target.checked ? ['6'] : [])}
                        label="None of the Above"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button color="primary" outline type="button" block onClick={onBack}>
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={!symptoms?.length}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default SecondaryAssessment;
